import { HubConnection, HubConnectionBuilder } from "@aspnet/signalr";
import React, { useState, useEffect, useRef } from "react";
import config from "../../config";
import { EMoveArmDirection } from "../../Model/EMoveImagingArmButton";
import { ENotificationType } from "../../Model/ENotificationType";
import { EArmType } from "../../Model/Waypoint";
import { sendNotification } from "../../redux/actions/NotificationAction";
import { getModuleStateById } from "../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import ArmMover from "./ArmMover";

const T_SPEED = "T_SPEED";
const E_SPEED = "E_SPEED";
const Z_SPEED = "Z_SPEED";

function getSpeed(key: string) {
  const value = localStorage.getItem(key);
  if (value != null) {
    return Number.parseInt(value);
  } else {
    return 3;
  }
}

interface HubRefParams {
  hubConnection: HubConnection | null;
}

const startSignalRConnection = (connection: HubConnection) =>
  connection
    .start()
    .then(() => {
      console.info("Arm SignalR Connected");
    })
    .catch((err) =>
      sendNotification(
        ENotificationType.Error,
        "Arm SignalR Connection Error: " + err
      )
    );

type Props = {
  moduleIdentifier: string;
  armType: EArmType;
};

export default function CylindricalArmMover({
  moduleIdentifier,
  armType,
}: Props) {
  const [tSpeed, setTSpeed] = useState(getSpeed(T_SPEED));
  const [eSpeed, setESpeed] = useState(getSpeed(E_SPEED));
  const [zSpeed, setZSpeed] = useState(getSpeed(Z_SPEED));

  const hubRef = useRef<HubRefParams>({ hubConnection: null });

  useEffect(() => {
    localStorage.setItem(T_SPEED, tSpeed.toString());
  }, [tSpeed]);

  useEffect(() => {
    localStorage.setItem(E_SPEED, eSpeed.toString());
  }, [eSpeed]);

  useEffect(() => {
    localStorage.setItem(Z_SPEED, zSpeed.toString());
  }, [zSpeed]);

  useEffect(() => {
    const urlConnection = config.SIGNALR_URL + "/arm";
    const hubConnection = new HubConnectionBuilder()
      .withUrl(urlConnection)
      .build();
    hubRef.current.hubConnection = hubConnection;

    hubConnection.onclose(() => {
      console.log("SignalR connection lost");
      setTimeout(() => {
        console.log("SignalR connection restarted");
        if (hubConnection != null) {
          startSignalRConnection(hubConnection);
        }
      }, 5000);
    });

    startSignalRConnection(hubConnection);

    return () => {
      hubConnection?.stop();
    };
  }, []);

  const psrModuleState = useTypedSelector(
    (state) => getModuleStateById(state)[moduleIdentifier]?.psr
  );
  if (psrModuleState == null) {
    throw new Error("Psr Module State is null");
  }

  const getDirectionalSpeed = (direction: EMoveArmDirection) => {
    switch (direction) {
      case EMoveArmDirection.ClockWise:
      case EMoveArmDirection.CounterClockWise:
        return tSpeed;

      case EMoveArmDirection.North:
      case EMoveArmDirection.South:
        return eSpeed;

      case EMoveArmDirection.Up:
      case EMoveArmDirection.Down:
        return zSpeed;
    }

    return 1;
  };

  const onButtonDown = (direction: EMoveArmDirection) => {
    hubRef.current.hubConnection?.send(
      "ArmMoveToTravelLimit",
      moduleIdentifier,
      armType,
      direction,
      getDirectionalSpeed(direction)
    );
  };

  const onButtonUp = () => {
    hubRef.current.hubConnection?.send("ArmStop", moduleIdentifier);
  };

  return ArmMover.getCylindricalArmMover(
    {
      armName: EArmType[armType],
      axisLabel: ["T", "E", "Z"],
      onButtonDown: onButtonDown,
      onButtonUp: onButtonUp,
      t: psrModuleState.cylindricalPosition.t,
      e: psrModuleState.cylindricalPosition.e,
      z: psrModuleState.cylindricalPosition.z,
      tSpeed: tSpeed,
      eSpeed: eSpeed,
      zSpeed: zSpeed,
      setESpeed: setESpeed,
      setTSpeed: setTSpeed,
      setZSpeed: setZSpeed
    }
  );
}
